import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell, BarChart,
} from "recharts";
import { Wallet, Landmark, TrendingUp, AlertTriangle, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { fmtMoney, fmtNum, pnlColor, toCsv } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import DataTable from "../components/DataTable";
import KpiCard from "../components/KpiCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CONF_COLORS = { Confirmed: "#059669", Probable: "#d97706", Lead: "#94a3b8" };

function fmtDay(d) {
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// ---------------- Actual statement ----------------
function ActualView({ entity }) {
  const now = new Date();
  const [preset, setPreset] = useState("month");
  const [start, setStart] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [end, setEnd] = useState(now.toISOString().slice(0, 10));
  const [compare, setCompare] = useState(false);

  const prevRange = useMemo(() => {
    const s = new Date(start), e = new Date(end);
    const span = e - s;
    return { ps: new Date(s - span - 86400000).toISOString().slice(0, 10), pe: new Date(s - 86400000).toISOString().slice(0, 10) };
  }, [start, end]);

  const applyPreset = (p) => {
    setPreset(p);
    const y = now.getFullYear(), m = now.getMonth();
    if (p === "month") { setStart(new Date(y, m, 1).toISOString().slice(0, 10)); setEnd(now.toISOString().slice(0, 10)); }
    if (p === "quarter") { const qs = Math.floor(m / 3) * 3; setStart(new Date(y, qs, 1).toISOString().slice(0, 10)); setEnd(now.toISOString().slice(0, 10)); }
    if (p === "ytd") { setStart(new Date(y, 0, 1).toISOString().slice(0, 10)); setEnd(now.toISOString().slice(0, 10)); }
  };

  const { data } = useQuery({
    queryKey: ["ff-actual", entity, start, end, compare],
    queryFn: () => api.get("/fund-flow/actual", {
      entity_id: entity, start: start + "T00:00:00+00:00", end: end + "T23:59:59+00:00",
      prev_start: compare ? prevRange.ps + "T00:00:00+00:00" : undefined,
      prev_end: compare ? prevRange.pe + "T23:59:59+00:00" : undefined,
    }),
  });
  const cur = data?.current, prior = data?.prior;
  const priorMap = useMemo(() => {
    const m = {};
    if (prior) [...prior.sources, ...prior.applications].forEach((l) => { m[l.label] = l.amount; });
    return m;
  }, [prior]);

  if (!cur) return <div className="text-slate-400">Computing statement…</div>;

  const bridge = [
    { name: "Sources", value: cur.total_sources, fill: "#059669" },
    { name: "Applications", value: -cur.total_applications, fill: "#e11d48" },
    { name: "Net Change", value: cur.net_change, fill: cur.net_change >= 0 ? "#2563eb" : "#d97706" },
  ];

  const Section = ({ title, rows, color }) => (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
      <div className={`px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 font-bold text-sm ${color}`}>{title}</div>
      <table className="w-full">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-slate-800/60">
              <td className="px-4 py-2 text-sm">{r.label}</td>
              {compare && <td className="px-3 py-2 text-right font-mono text-xs text-slate-400">{priorMap[r.label] != null ? fmtMoney(priorMap[r.label]) : "-"}</td>}
              <td className="px-4 py-2 text-right font-mono text-sm font-semibold">{fmtMoney(r.amount)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td className="px-4 py-4 text-sm text-slate-400">No movements in period</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="ff-actual-view">
      <div className="flex flex-wrap items-end gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
        <div>
          <Label className="text-xs">Period</Label>
          <div className="flex gap-1 mt-1">
            {["month", "quarter", "ytd"].map((p) => (
              <button key={p} data-testid={`ff-preset-${p}`} onClick={() => applyPreset(p)}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded border ${preset === p ? "bg-orange-600 text-white border-orange-600" : "border-slate-200 dark:border-slate-800 text-slate-500"}`}>
                {p === "ytd" ? "YTD" : p[0].toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div><Label className="text-xs">From</Label><Input data-testid="ff-start" type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreset("custom"); }} className="h-9 w-40" /></div>
        <div><Label className="text-xs">To</Label><Input data-testid="ff-end" type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreset("custom"); }} className="h-9 w-40" /></div>
        <div className="flex items-center gap-2 pb-1"><Switch data-testid="ff-compare" checked={compare} onCheckedChange={setCompare} /><Label className="text-xs">Compare vs prior period</Label></div>
        <Button data-testid="ff-actual-export" variant="outline" size="sm" className="ml-auto"
          onClick={() => toCsv([...cur.sources.map((s) => ({ section: "Source", ...s })), ...cur.applications.map((a) => ({ section: "Application", ...a }))], "actual_fund_flow")}>
          Export CMA-style
        </Button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-4">
        <h3 className="font-bold text-sm mb-3">Sources → Applications Bridge</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={bridge}>
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
            <Tooltip formatter={(v) => fmtMoney(v)} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>{bridge.map((b, i) => <Cell key={i} fill={b.fill} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Sources of Funds" rows={cur.sources} color="text-emerald-600" />
        <Section title="Applications of Funds" rows={cur.applications} color="text-rose-600" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="ff-total-sources" label="Total Sources" value={fmtMoney(cur.total_sources)} color="emerald" Icon={ArrowUpRight} />
        <KpiCard testid="ff-total-applications" label="Total Applications" value={fmtMoney(cur.total_applications)} color="rose" Icon={ArrowDownRight} />
        <KpiCard testid="ff-net-change" label="Net Change in Funds" value={fmtMoney(cur.net_change)} color="blue" Icon={TrendingUp} />
        <KpiCard testid="ff-cash-move" label="Cash & Bank Movement" value={fmtMoney(cur.cash_movement)} sub="GL-reconciled" color="teal" Icon={Wallet} />
      </div>
    </div>
  );
}

// ---------------- Forecast view ----------------
function ForecastView({ entity }) {
  const [horizon, setHorizon] = useState(90);
  const [bucket, setBucket] = useState("week");
  const [conf, setConf] = useState({ Confirmed: true, Probable: true });
  const [delayDays, setDelayDays] = useState(0);
  const [adverseHedge, setAdverseHedge] = useState(0);

  const { data } = useQuery({ queryKey: ["ff-forecast", entity], queryFn: () => api.get("/fund-flow/forecast", { entity_id: entity }) });

  const processed = useMemo(() => {
    if (!data) return null;
    const now = new Date();
    const horizonEnd = new Date(now.getTime() + horizon * 86400000);
    let evs = data.events
      .filter((e) => conf[e.confidence])
      .map((e) => {
        let date = new Date(e.date);
        let amount = e.amount;
        if (delayDays && e.category === "Sale Collection") date = new Date(date.getTime() + delayDays * 86400000);
        if (adverseHedge && e.category === "Hedge Variation Margin" && e.hedge_meta) {
          const m = e.hedge_meta;
          const move = m.notional * (adverseHedge / 100);
          amount = -Math.abs(move); // adverse => variation margin outflow
        }
        return { ...e, _date: date, _amount: amount };
      })
      .filter((e) => e._date <= horizonEnd);
    evs.sort((a, b) => a._date - b._date);

    // buckets
    const buckets = {};
    const key = (d) => bucket === "day" ? d.toISOString().slice(0, 10)
      : (() => { const wk = new Date(d); wk.setDate(wk.getDate() - wk.getDay()); return wk.toISOString().slice(0, 10); })();
    evs.forEach((e) => {
      const k = key(e._date);
      buckets[k] = buckets[k] || { date: k, inflow: 0, outflow: 0 };
      if (e._amount >= 0) buckets[k].inflow += e._amount; else buckets[k].outflow += e._amount;
    });
    evs = evs.map((e, i) => ({ ...e, _uid: `${e.category}-${e.source_id}-${e.date}-${i}` }));
    let running = data.opening_cash;
    const rows = Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date)).map((b) => {
      running += b.inflow + b.outflow;
      const headroom = data.facility_available;
      return { ...b, net: b.inflow + b.outflow, closing: Math.round(running * 100) / 100,
               breach: running < 0, headroom_breach: running < -headroom };
    });
    const breaches = rows.filter((r) => r.breach || r.headroom_breach);
    const totalIn = evs.filter((e) => e._amount > 0).reduce((s, e) => s + e._amount, 0);
    const totalOut = evs.filter((e) => e._amount < 0).reduce((s, e) => s + e._amount, 0);
    return { rows, evs, breaches, totalIn, totalOut, projectedClose: running };
  }, [data, horizon, bucket, conf, delayDays, adverseHedge]);

  if (!data) return <div className="text-slate-400">Building liquidity forecast…</div>;
  const p = processed;

  return (
    <div className="space-y-4" data-testid="ff-forecast-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="ff-opening-cash" label="Opening Cash & Bank" value={fmtMoney(data.opening_cash)} color="teal" Icon={Wallet} />
        <KpiCard testid="ff-facility-limit" label="Facility Limit" value={fmtMoney(data.facility_limit)} sub={`Drawn ${fmtMoney(data.facility_drawn)}`} color="blue" Icon={Landmark} />
        <KpiCard testid="ff-headroom" label="Available Headroom" value={fmtMoney(data.facility_available)} color="emerald" Icon={ArrowUpRight} />
        <KpiCard testid="ff-projected-close" label={`Projected Close (${horizon}d)`} value={fmtMoney(p.projectedClose)} color={p.projectedClose >= 0 ? "purple" : "rose"} Icon={TrendingUp} />
      </div>

      {p.breaches.length > 0 && (
        <div data-testid="ff-alerts-strip" className="rounded-lg border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-3">
          <p className="flex items-center gap-2 font-bold text-rose-700 dark:text-rose-300 text-sm"><AlertTriangle className="h-4 w-4" /> Liquidity shortfall / facility breach detected</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {p.breaches.slice(0, 6).map((b, i) => (
              <span key={i} className="text-xs font-mono bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900 rounded px-2 py-1">
                {fmtDay(b.date)} → {fmtMoney(b.closing)}{b.headroom_breach ? " (beyond headroom)" : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm">Projected Cash Position</h3>
            <div className="flex gap-1">
              {[30, 60, 90].map((h) => (
                <button key={h} data-testid={`ff-horizon-${h}`} onClick={() => setHorizon(h)}
                  className={`text-xs font-semibold px-2 py-1 rounded border ${horizon === h ? "bg-orange-600 text-white border-orange-600" : "border-slate-200 dark:border-slate-800 text-slate-500"}`}>{h}d</button>
              ))}
              {["week", "day"].map((b) => (
                <button key={b} data-testid={`ff-bucket-${b}`} onClick={() => setBucket(b)}
                  className={`text-xs font-semibold px-2 py-1 rounded border ${bucket === b ? "bg-slate-700 text-white border-slate-700" : "border-slate-200 dark:border-slate-800 text-slate-500"}`}>{b}</button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={p.rows}>
              <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
              <Tooltip formatter={(v) => fmtMoney(v)} labelFormatter={fmtDay} />
              <ReferenceLine y={0} stroke="#e11d48" strokeDasharray="3 3" />
              <Bar dataKey="inflow" fill="#059669" radius={[3, 3, 0, 0]} />
              <Bar dataKey="outflow" fill="#e11d48" radius={[0, 0, 3, 3]} />
              <Line type="monotone" dataKey="closing" stroke="#ea580c" strokeWidth={2.5} dot={{ r: 2 }} name="Projected Cash" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-3">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-3">
            <p className="text-xs font-bold uppercase text-slate-400 mb-2">Confidence Layers</p>
            {["Confirmed", "Probable"].map((c) => (
              <div key={c} className="flex items-center justify-between py-1">
                <span className="flex items-center gap-2 text-sm"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CONF_COLORS[c] }} />{c}</span>
                <Switch data-testid={`ff-conf-${c}`} checked={conf[c]} onCheckedChange={(v) => setConf((s) => ({ ...s, [c]: v }))} />
              </div>
            ))}
            <div className="flex items-center justify-between py-1 opacity-50">
              <span className="flex items-center gap-2 text-sm"><span className="h-2.5 w-2.5 rounded-full bg-slate-400" />Lead / Pipeline</span>
              <span className="text-[10px] text-slate-400">after CRM</span>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-3">
            <p className="text-xs font-bold uppercase text-slate-400 mb-2">Scenario / Stress</p>
            <Label className="text-xs">Delay collections (days): {delayDays}</Label>
            <Input data-testid="ff-delay" type="range" min="0" max="45" step="5" value={delayDays} onChange={(e) => setDelayDays(Number(e.target.value))} className="w-full" />
            <Label className="text-xs mt-2 block">Adverse hedge move: {adverseHedge}%</Label>
            <Input data-testid="ff-adverse" type="range" min="0" max="20" step="1" value={adverseHedge} onChange={(e) => setAdverseHedge(Number(e.target.value))} className="w-full" />
            {(delayDays > 0 || adverseHedge > 0) && <button data-testid="ff-reset-scenario" onClick={() => { setDelayDays(0); setAdverseHedge(0); }} className="mt-2 text-xs text-orange-600 font-semibold">Reset to base</button>}
          </div>
        </div>
      </div>

      <DataTable
        testid="ff-forecast-table"
        title="Forecast Line Items"
        columns={[
          { key: "date", label: "Expected Date", render: (r) => <span className="text-xs">{fmtDay(r._date)}</span> },
          { key: "category", label: "Category" },
          { key: "label", label: "Detail" },
          { key: "confidence", label: "Confidence", render: (r) => <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: CONF_COLORS[r.confidence] + "22", color: CONF_COLORS[r.confidence] }}>{r.confidence}</span> },
          { key: "_amount", label: "Amount", align: "right", render: (r) => <span className={pnlColor(r._amount) + " font-semibold"}>{fmtMoney(r._amount)}</span> },
        ]}
        rows={p.evs}
        rowKey="_uid"
        csvName="liquidity_forecast"
      />
    </div>
  );
}

export default function FundFlow() {
  const { entity } = useApp();
  return (
    <div data-testid="fundflow-page">
      <Tabs defaultValue="forecast">
        <TabsList className="mb-4">
          <TabsTrigger value="forecast" data-testid="ff-tab-forecast">Anticipated (Liquidity Forecast)</TabsTrigger>
          <TabsTrigger value="actual" data-testid="ff-tab-actual">Actual (Fund Flow Statement)</TabsTrigger>
        </TabsList>
        <TabsContent value="forecast"><ForecastView entity={entity} /></TabsContent>
        <TabsContent value="actual"><ActualView entity={entity} /></TabsContent>
      </Tabs>
    </div>
  );
}
